/**
 * Language and reading direction.
 *
 * Two languages, one dictionary, keyed by the English source string rather
 * than by an invented id: a missing Urdu entry then falls back to readable
 * English instead of a raw key, and a screen that has not been translated yet
 * still reads correctly. The English column is the source of truth — change a
 * sentence in a screen and its Urdu simply stops matching, which the
 * dictionary test catches.
 */

export const LANGUAGES = ['en', 'ur'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABEL: Record<Language, string> = {
  en: 'English',
  ur: 'اردو',
};

/** Short label for the header toggle: what a tap switches *to*. */
export const LANGUAGE_SHORT: Record<Language, string> = {
  en: 'EN',
  ur: 'اردو',
};

export const RTL_LANGUAGES: ReadonlySet<Language> = new Set<Language>(['ur']);

export function isRTLLanguage(language: Language): boolean {
  return RTL_LANGUAGES.has(language);
}

export function otherLanguage(language: Language): Language {
  return language === 'en' ? 'ur' : 'en';
}

export const LANGUAGE_STORAGE_KEY = 'meal4all.language';

/** Short month names per language, January first — used by dates and the calendar. */
export const MONTHS: Record<Language, readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
  ur: [
    'جنوری',
    'فروری',
    'مارچ',
    'اپریل',
    'مئی',
    'جون',
    'جولائی',
    'اگست',
    'ستمبر',
    'اکتوبر',
    'نومبر',
    'دسمبر',
  ],
};

/** Weekday initials, Monday first, matching the calendar's column order. */
export const WEEKDAYS: Record<Language, readonly string[]> = {
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ur: ['پیر', 'منگل', 'بدھ', 'جمعرات', 'جمعہ', 'ہفتہ', 'اتوار'],
};

export const CURRENCY: Record<Language, Record<string, string>> = {
  en: { PKR: 'Rs.' },
  ur: { PKR: 'روپے' },
};

export const PEOPLE_WORDS: Record<
  Language,
  { person: string; people: string; day: string; days: string; meal: string; meals: string }
> = {
  en: { person: 'Person', people: 'People', day: 'day', days: 'days', meal: 'meal', meals: 'meals' },
  ur: { person: 'فرد', people: 'افراد', day: 'دن', days: 'دن', meal: 'کھانا', meals: 'کھانے' },
};
